import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '../components/ui/accordion';
import SupportAssistant from '../components/SupportAssistant';
import {
  Zap, ArrowRight, ArrowUpRight, Check, Star,
  FileText, Bell, Calculator, CreditCard, Sparkles,
  LayoutDashboard, Receipt, FileSpreadsheet, Users, Wallet, BarChart3, Settings,
  Clock, TrendingUp, TrendingDown, MoreHorizontal, Download,
  Briefcase, HardHat, Stethoscope, Palette, Laptop, ShoppingBag,
  Menu, X, Eye, Shield, Lock, Server, ShieldCheck, Award,
} from 'lucide-react';

/* ━━━━━━━━━━━━━━━ DESIGN TOKENS — PISTACHIO LIGHT (EXACT reference) ━━━━━━━━━━━━━━━ */
const GREEN = '#6CC24A';             // primary brand — exact
const GREEN_DEEP = '#58A83A';         // hover / darker
const GREEN_SOFT = '#DFF7D8';         // soft bg wash — exact
const GREEN_GLOW = 'rgba(108, 194, 74, 0.35)';
const BG = '#FAFFF7';                 // off-white with green tint — exact
const BG_SOFT = '#F3FAEE';            // slightly deeper for alt sections
const CARD = '#FFFFFF';
const BORDER = '#E5E7EB';
const TXT = '#111827';                // exact
const TXT_MUTED = '#6B7280';          // exact
const TXT_DIM = '#9CA3AF';

/* ━━━━━━━━━━━━━━━ ANIMATIONS ━━━━━━━━━━━━━━━ */
const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.7, delay: 0.05 * i, ease: [0.22, 1, 0.36, 1] },
  }),
};

/* ━━━━━━━━━━━━━━━ PRIMITIVES ━━━━━━━━━━━━━━━ */
const Section = ({ children, className = '', bg, id, testId }) => (
  <section
    id={id}
    data-testid={testId}
    className={`relative px-5 sm:px-6 ${className}`}
    style={{
      paddingTop: '110px',
      paddingBottom: '110px',
      backgroundColor: bg || 'transparent',
    }}
  >
    {children}
  </section>
);

const Container = ({ children, narrow = false, wide = false, className = '' }) => (
  <div
    className={`relative mx-auto ${className}`}
    style={{ maxWidth: narrow ? '720px' : wide ? '1280px' : '1140px' }}
  >
    {children}
  </div>
);

const Label = ({ children }) => (
  <span
    className="text-[11.5px] font-bold uppercase"
    style={{ color: GREEN_DEEP, letterSpacing: '0.22em' }}
  >
    {children}
  </span>
);

const PrimaryBtn = ({ to, children, testId, large = false }) => (
  <Link
    to={to}
    data-testid={testId}
    className="group inline-flex items-center justify-center gap-2 rounded-full font-semibold transition-[transform,box-shadow,background-color] duration-300 hover:-translate-y-0.5"
    style={{
      backgroundColor: GREEN,
      color: '#fff',
      height: large ? '54px' : '48px',
      padding: large ? '0 26px' : '0 22px',
      fontSize: large ? '15px' : '14px',
      boxShadow: `0 10px 28px -10px ${GREEN_GLOW}`,
    }}
    onMouseOver={(e) => {
      e.currentTarget.style.backgroundColor = GREEN_DEEP;
      e.currentTarget.style.boxShadow = `0 16px 40px -10px ${GREEN_GLOW}`;
    }}
    onMouseOut={(e) => {
      e.currentTarget.style.backgroundColor = GREEN;
      e.currentTarget.style.boxShadow = `0 10px 28px -10px ${GREEN_GLOW}`;
    }}
  >
    {children}
    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
  </Link>
);

const GhostBtn = ({ to, children, testId, large = false }) => (
  <Link
    to={to}
    data-testid={testId}
    className="inline-flex items-center justify-center gap-2 rounded-full font-semibold transition-colors duration-200"
    style={{
      backgroundColor: '#fff',
      color: TXT,
      height: large ? '54px' : '48px',
      padding: large ? '0 26px' : '0 22px',
      fontSize: large ? '15px' : '14px',
      border: `1px solid ${BORDER}`,
    }}
    onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#F9FAFB')}
    onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#fff')}
  >
    {children}
  </Link>
);

/* ━━━━━━━━━━━━━━━ NAVBAR ━━━━━━━━━━━━━━━ */
const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <nav
      data-testid="landing-navbar"
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
      style={{
        backgroundColor: scrolled ? 'rgba(255,255,255,0.92)' : 'transparent',
        backdropFilter: scrolled ? 'blur(16px)' : 'none',
        borderBottom: scrolled ? `1px solid ${BORDER}` : '1px solid transparent',
      }}
    >
      <Container>
        <div className="flex items-center justify-between h-[64px] sm:h-[72px]">
          <Link to="/" className="flex items-center gap-2.5" data-testid="nav-logo-link">
            <img
              src="/smartfacture-logo.png"
              alt="SmartFacture AI"
              className="h-9 sm:h-10 w-auto"
              style={{ display: 'block' }}
            />
          </Link>

          <div className="hidden lg:flex items-center gap-8 text-[14px] font-medium" style={{ color: TXT_MUTED }}>
            <a href="#fonctionnalites" data-testid="nav-features" className="hover:text-slate-900 transition-colors">Fonctionnalités</a>
            <a href="#tarifs" data-testid="nav-pricing" className="hover:text-slate-900 transition-colors">Tarifs</a>
            <a href="#ressources" data-testid="nav-resources" className="hover:text-slate-900 transition-colors">Ressources</a>
            <a href="#apropos" data-testid="nav-about" className="hover:text-slate-900 transition-colors">À propos</a>
          </div>

          <div className="flex items-center gap-2">
            <Link
              to="/login"
              data-testid="nav-login-btn"
              className="hidden sm:inline-flex h-10 items-center px-3.5 rounded-full text-[13.5px] font-semibold transition-colors"
              style={{ color: TXT_MUTED }}
              onMouseOver={(e) => (e.currentTarget.style.color = TXT)}
              onMouseOut={(e) => (e.currentTarget.style.color = TXT_MUTED)}
            >
              Se connecter
            </Link>
            <Link
              to="/register"
              data-testid="nav-register-btn"
              className="inline-flex items-center gap-1.5 h-10 px-4 sm:px-5 rounded-full text-[13.5px] font-semibold transition-colors"
              style={{ backgroundColor: GREEN, color: '#fff', boxShadow: `0 6px 18px -6px ${GREEN_GLOW}` }}
              onMouseOver={(e) => (e.currentTarget.style.backgroundColor = GREEN_DEEP)}
              onMouseOut={(e) => (e.currentTarget.style.backgroundColor = GREEN)}
            >
              Essayer gratuitement
            </Link>
            <button
              data-testid="nav-mobile-toggle"
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-full"
              style={{ color: TXT, backgroundColor: 'rgba(0,0,0,0.04)' }}
              aria-label="Menu"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {open && (
          <div
            data-testid="nav-mobile-menu"
            className="lg:hidden pb-4 pt-2 space-y-1 text-[15px] font-medium rounded-2xl mt-2 mb-3 px-2"
            style={{ color: TXT_MUTED, backgroundColor: '#fff', border: `1px solid ${BORDER}`, boxShadow: '0 20px 50px -20px rgba(15,23,42,0.15)' }}
          >
            {[
              { href: '#fonctionnalites', label: 'Fonctionnalités' },
              { href: '#tarifs', label: 'Tarifs' },
              { href: '#ressources', label: 'Ressources' },
              { href: '#apropos', label: 'À propos' },
            ].map((i) => (
              <a key={i.href} href={i.href} onClick={() => setOpen(false)} className="block px-3 py-2.5 rounded-xl hover:bg-slate-50">
                {i.label}
              </a>
            ))}
            <Link to="/login" className="block px-3 py-2.5 rounded-xl hover:bg-slate-50">Se connecter</Link>
          </div>
        )}
      </Container>
    </nav>
  );
};

/* ━━━━━━━━━━━━━━━ HERO DASHBOARD MOCKUP (green accents) ━━━━━━━━━━━━━━━ */
const HeroDashboard = () => (
  <div className="relative w-full max-w-[620px] mx-auto">
    {/* Soft glow behind */}
    <div className="absolute inset-0 -z-10">
      <div className="absolute -top-16 -right-16 w-80 h-80 rounded-full" style={{ backgroundColor: GREEN_SOFT, filter: 'blur(80px)' }} />
      <div className="absolute -bottom-10 -left-10 w-72 h-72 rounded-full opacity-60" style={{ backgroundColor: '#FEF3C7', filter: 'blur(80px)' }} />
    </div>

    <div
      className="rounded-[24px] bg-white overflow-hidden"
      style={{ border: `1px solid ${BORDER}`, boxShadow: '0 30px 80px -30px rgba(15,23,42,0.2)' }}
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-5 py-3.5" style={{ borderBottom: `1px solid ${BORDER}` }}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: GREEN }}>
            <Zap className="h-4 w-4 text-white" />
          </div>
          <span className="text-[13px] font-bold" style={{ color: TXT }}>SmartFacture</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: '#F87171' }} />
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: '#FBBF24' }} />
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: GREEN }} />
        </div>
      </div>

      {/* Greeting */}
      <div className="px-6 pt-5 pb-4">
        <div className="text-[12.5px]" style={{ color: TXT_MUTED }}>Bonjour, Alex 👋</div>
        <div className="text-[11px] mt-0.5" style={{ color: TXT_DIM }}>Voici un aperçu de votre activité</div>
      </div>

      {/* KPIs */}
      <div className="px-5 grid grid-cols-3 gap-2.5">
        {[
          { label: "Chiffre d'affaires", value: '12 450 €', trend: '+24,5%', up: true, icon: TrendingUp },
          { label: 'En attente', value: '8', sub: '2 850 €', icon: Clock, color: '#FBBF24' },
          { label: 'Factures payées', value: '23', sub: '+15,2%', up: true, icon: Check, color: GREEN },
        ].map((kpi, i) => {
          const I = kpi.icon;
          return (
            <div key={i} className="rounded-xl p-3" style={{ backgroundColor: BG_SOFT, border: `1px solid ${BORDER}` }}>
              <div className="flex items-start justify-between mb-2">
                <span className="text-[9.5px] font-semibold uppercase tracking-wider" style={{ color: TXT_DIM }}>{kpi.label}</span>
                <I className="h-3 w-3" style={{ color: kpi.color || TXT_DIM }} />
              </div>
              <div className="text-[15.5px] font-bold tabular-nums" style={{ color: TXT }}>{kpi.value}</div>
              {kpi.trend && (
                <div className="text-[10px] font-semibold mt-0.5" style={{ color: GREEN_DEEP }}>{kpi.trend} ce mois</div>
              )}
              {kpi.sub && !kpi.trend && (
                <div className="text-[10px] mt-0.5" style={{ color: TXT_DIM }}>{kpi.sub}</div>
              )}
            </div>
          );
        })}
      </div>

      {/* Chart */}
      <div className="px-5 pt-5 pb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[11.5px] font-semibold" style={{ color: TXT_MUTED }}>Évolution du chiffre d'affaires</span>
          <span className="text-[10px] rounded-md px-2 py-0.5" style={{ color: TXT_MUTED, backgroundColor: BG_SOFT, border: `1px solid ${BORDER}` }}>Ce mois</span>
        </div>
        <div className="relative h-[88px]">
          <svg viewBox="0 0 400 90" className="w-full h-full" preserveAspectRatio="none">
            <defs>
              <linearGradient id="chartGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={GREEN} stopOpacity="0.35" />
                <stop offset="100%" stopColor={GREEN} stopOpacity="0" />
              </linearGradient>
            </defs>
            <path
              d="M0 70 C 40 65, 60 55, 80 55 C 110 55, 130 45, 160 42 C 190 40, 210 35, 240 30 C 270 25, 290 22, 320 18 C 350 14, 380 10, 400 12 L 400 90 L 0 90 Z"
              fill="url(#chartGrad)"
            />
            <path
              d="M0 70 C 40 65, 60 55, 80 55 C 110 55, 130 45, 160 42 C 190 40, 210 35, 240 30 C 270 25, 290 22, 320 18 C 350 14, 380 10, 400 12"
              stroke={GREEN_DEEP}
              strokeWidth="2"
              fill="none"
              strokeLinecap="round"
            />
            {/* Dots */}
            {[80, 160, 240, 320].map((x, i) => {
              const y = [55, 42, 30, 18][i];
              return <circle key={i} cx={x} cy={y} r="3" fill="#fff" stroke={GREEN_DEEP} strokeWidth="1.5" />;
            })}
          </svg>
        </div>
        <div className="flex justify-between text-[9px] mt-1" style={{ color: TXT_DIM }}>
          <span>1 Avr</span><span>8 Avr</span><span>15 Avr</span><span>22 Avr</span><span>29 Avr</span>
        </div>
      </div>

      {/* Recent */}
      <div className="px-5 pb-5">
        <div className="flex items-center justify-between mb-2.5">
          <span className="text-[11.5px] font-semibold" style={{ color: TXT_MUTED }}>Activité récente</span>
          <span className="text-[10px] font-semibold" style={{ color: GREEN_DEEP }}>Voir tout →</span>
        </div>
        <div className="space-y-1.5">
          {[
            { name: 'Facture F-2026-042 · payée', client: 'Dupont SARL', time: 'il y a 2 h', amount: '1 250 €', ok: true },
            { name: 'Facture F-2026-041 · envoyée', client: 'à Martin & Co', time: 'il y a 5 h', amount: '860 €', ok: false },
            { name: 'Paiement reçu', client: 'de Bernard SAS', time: 'il y a 8 h', amount: '1 250 €', ok: true },
          ].map((a, i) => (
            <div key={i} className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg" style={{ backgroundColor: BG_SOFT }}>
              <div
                className="w-7 h-7 rounded-md flex items-center justify-center shrink-0"
                style={{ backgroundColor: a.ok ? `${GREEN}22` : '#FBBF2422' }}
              >
                <FileText className="h-3.5 w-3.5" style={{ color: a.ok ? GREEN_DEEP : '#D97706' }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[11px] font-semibold truncate" style={{ color: TXT }}>{a.name}</div>
                <div className="text-[9.5px]" style={{ color: TXT_DIM }}>{a.client} · {a.time}</div>
              </div>
              <span className="text-[11px] font-bold tabular-nums" style={{ color: TXT }}>{a.amount}</span>
            </div>
          ))}
        </div>
        <button className="mt-3 w-full rounded-lg py-2 text-[11px] font-semibold transition-colors" style={{ backgroundColor: GREEN_SOFT, color: GREEN_DEEP }}>
          Voir toutes les activités
        </button>
      </div>
    </div>

    {/* Floating badge */}
    <motion.div
      animate={{ y: [0, -8, 0] }}
      transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
      className="absolute -top-5 -right-4 sm:-right-8 rounded-2xl bg-white px-3.5 py-2.5 flex items-center gap-2.5"
      style={{ border: `1px solid ${BORDER}`, boxShadow: '0 14px 30px -10px rgba(15,23,42,0.18)' }}
    >
      <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: GREEN_SOFT }}>
        <Check className="h-4 w-4" style={{ color: GREEN_DEEP, strokeWidth: 3 }} />
      </div>
      <div>
        <div className="text-[9.5px] font-bold uppercase tracking-wider" style={{ color: TXT_DIM }}>Factur-X</div>
        <div className="text-[11.5px] font-bold" style={{ color: TXT }}>Conforme 2026</div>
      </div>
    </motion.div>
  </div>
);

/* ━━━━━━━━━━━━━━━ HERO ━━━━━━━━━━━━━━━ */
const Hero = () => (
  <section
    data-testid="hero-section"
    className="relative px-5 sm:px-6 overflow-hidden"
    style={{
      paddingTop: '140px',
      paddingBottom: '90px',
      background: `linear-gradient(180deg, ${GREEN_SOFT} 0%, #FFFFFF 85%)`,
    }}
  >
    {/* Subtle dot pattern */}
    <div
      className="absolute inset-0 opacity-[0.35] pointer-events-none"
      style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(15,23,42,0.06) 1px, transparent 0)',
        backgroundSize: '28px 28px',
        maskImage: 'radial-gradient(ellipse 80% 60% at 50% 40%, black 30%, transparent 80%)',
      }}
    />

    <Container wide>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <motion.div
          initial="hidden"
          animate="show"
          variants={{ show: { transition: { staggerChildren: 0.08 } } }}
        >
          {/* Badge */}
          <motion.div variants={fadeUp} className="inline-flex mb-7">
            <span
              data-testid="hero-badge"
              className="inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-[12.5px] font-semibold"
              style={{ backgroundColor: GREEN_SOFT, color: GREEN_DEEP }}
            >
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full opacity-75 animate-ping" style={{ backgroundColor: GREEN }} />
                <span className="relative inline-flex rounded-full h-2 w-2" style={{ backgroundColor: GREEN }} />
              </span>
              Conforme e-facturation · 1ᵉʳ septembre 2026
            </span>
          </motion.div>

          {/* Title */}
          <motion.h1
            variants={fadeUp}
            className="font-bold text-[40px] sm:text-[48px] lg:text-[56px]"
            style={{ color: TXT, letterSpacing: '-0.035em', lineHeight: 1.08 }}
          >
            Facturez plus vite,<br />
            gérez mieux,{' '}
            <span style={{ color: GREEN_DEEP }}>gagnez plus.</span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            variants={fadeUp}
            className="mt-6 text-[15.5px] sm:text-[16px]"
            style={{ color: TXT_MUTED, maxWidth: '500px', lineHeight: 1.6 }}
          >
            SmartFacture AI simplifie la facturation, le suivi des paiements
            et la gestion de votre activité grâce à l'intelligence artificielle.
          </motion.p>

          {/* CTAs */}
          <motion.div variants={fadeUp} className="mt-9 flex flex-col sm:flex-row gap-3">
            <PrimaryBtn to="/register" testId="hero-cta-register" large>
              Essayer gratuitement
            </PrimaryBtn>
            <GhostBtn to="/demo" testId="hero-cta-demo" large>
              <Eye className="h-4 w-4" />
              Voir la démo
            </GhostBtn>
          </motion.div>

          {/* Micro-proof */}
          <motion.div
            variants={fadeUp}
            data-testid="hero-trust"
            className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-[13px]"
            style={{ color: TXT_MUTED }}
          >
            <span className="inline-flex items-center gap-1.5">
              <Check className="h-4 w-4" style={{ color: GREEN_DEEP, strokeWidth: 3 }} />
              14 jours gratuits
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Check className="h-4 w-4" style={{ color: GREEN_DEEP, strokeWidth: 3 }} />
              Aucune carte bancaire
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Check className="h-4 w-4" style={{ color: GREEN_DEEP, strokeWidth: 3 }} />
              Annulation à tout moment
            </span>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
        >
          <HeroDashboard />
        </motion.div>
      </div>
    </Container>
  </section>
);

/* ━━━━━━━━━━━━━━━ MADE IN FRANCE STRIP (no fake partners) ━━━━━━━━━━━━━━━ */
const TrustStrip = () => (
  <section className="px-5 sm:px-6 py-10 sm:py-12" style={{ backgroundColor: '#fff', borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}` }} data-testid="trust-strip">
    <Container wide>
      <div className="flex flex-col items-center gap-5 sm:flex-row sm:justify-center sm:gap-12 text-center">
        <div className="flex items-center gap-3">
          <span className="text-[26px]">🇫🇷</span>
          <div className="text-left">
            <div className="text-[13.5px] font-bold" style={{ color: TXT }}>Conçu et hébergé en France</div>
            <div className="text-[11.5px]" style={{ color: TXT_MUTED }}>Données stockées chez OVHcloud · Roubaix</div>
          </div>
        </div>
        <div className="hidden sm:block w-px h-10" style={{ backgroundColor: BORDER }} />
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: GREEN_SOFT }}>
            <Shield className="h-5 w-5" style={{ color: GREEN_DEEP }} />
          </div>
          <div className="text-left">
            <div className="text-[13.5px] font-bold" style={{ color: TXT }}>Conforme RGPD &amp; Factur-X</div>
            <div className="text-[11.5px]" style={{ color: TXT_MUTED }}>Norme EN16931 · Chiffrement AES-256</div>
          </div>
        </div>
        <div className="hidden sm:block w-px h-10" style={{ backgroundColor: BORDER }} />
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: GREEN_SOFT }}>
            <Lock className="h-5 w-5" style={{ color: GREEN_DEEP }} />
          </div>
          <div className="text-left">
            <div className="text-[13.5px] font-bold" style={{ color: TXT }}>Sécurité bancaire</div>
            <div className="text-[11.5px]" style={{ color: TXT_MUTED }}>2FA · Sessions chiffrées</div>
          </div>
        </div>
      </div>
    </Container>
  </section>
);

/* ━━━━━━━━━━━━━━━ FULL DASHBOARD PREVIEW SECTION ━━━━━━━━━━━━━━━ */
const FEATURE_ICONS = [
  { icon: FileText, title: 'Facturation simple', desc: 'Créez et envoyez vos factures en quelques clics.' },
  { icon: TrendingUp, title: 'Suivi en temps réel', desc: 'Suivez vos paiements et relances automatiquement.' },
  { icon: BarChart3, title: 'Rapports intelligents', desc: 'Analysez votre activité avec des rapports clairs et puissants.' },
  { icon: Sparkles, title: 'IA intégrée', desc: "L'intelligence artificielle vous aide à gagner du temps chaque jour." },
];

const DashboardPreview = () => (
  <Section id="fonctionnalites" bg={BG_SOFT} testId="dashboard-preview">
    <Container wide>
      <div className="text-center mb-14 mx-auto" style={{ maxWidth: '680px' }}>
        <Label>Tableau de bord</Label>
        <h2 className="mt-4 text-[34px] sm:text-[42px] lg:text-[48px] font-bold" style={{ color: TXT, letterSpacing: '-0.035em', lineHeight: 1.1 }}>
          Tout ce qui compte.<br />
          <span style={{ color: GREEN_DEEP }}>En un seul coup d'œil.</span>
        </h2>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.8 }}
        className="rounded-[28px] bg-white overflow-hidden"
        style={{ border: `1px solid ${BORDER}`, boxShadow: '0 40px 90px -40px rgba(15,23,42,0.22)' }}
      >
        {/* 4 Feature icons row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 px-6 py-8 gap-6" style={{ borderBottom: `1px solid ${BORDER}` }}>
          {FEATURE_ICONS.map((f, i) => {
            const I = f.icon;
            return (
              <div key={i} className="flex flex-col items-start">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3" style={{ backgroundColor: GREEN_SOFT }}>
                  <I className="h-5 w-5" style={{ color: GREEN_DEEP }} />
                </div>
                <div className="text-[14px] font-bold" style={{ color: TXT }}>{f.title}</div>
                <div className="text-[12px] mt-1" style={{ color: TXT_MUTED, lineHeight: 1.5 }}>{f.desc}</div>
              </div>
            );
          })}
        </div>

        <div className="flex min-h-[520px]">
          {/* Sidebar */}
          <div className="w-[210px] shrink-0 hidden md:flex flex-col py-5 px-3" style={{ backgroundColor: BG_SOFT, borderRight: `1px solid ${BORDER}` }}>
            <div className="flex items-center gap-2 px-2 mb-6">
              <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: GREEN }}>
                <Zap className="h-3.5 w-3.5 text-white" />
              </div>
              <span className="text-[13px] font-bold" style={{ color: TXT }}>SmartFacture</span>
              <span className="text-[9px] font-bold px-1 py-0.5 rounded" style={{ backgroundColor: GREEN_SOFT, color: GREEN_DEEP }}>AI</span>
            </div>
            <div className="space-y-0.5 text-[13px] flex-1">
              {[
                { icon: LayoutDashboard, label: 'Tableau de bord', active: true },
                { icon: Receipt, label: 'Factures' },
                { icon: FileSpreadsheet, label: 'Devis' },
                { icon: Wallet, label: 'Dépenses' },
                { icon: Users, label: 'Clients' },
                { icon: CreditCard, label: 'Paiements' },
                { icon: BarChart3, label: 'Rapports' },
                { icon: Settings, label: 'Paramètres' },
              ].map((item, i) => {
                const I = item.icon;
                return (
                  <div
                    key={i}
                    className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg font-medium"
                    style={{
                      backgroundColor: item.active ? GREEN_SOFT : 'transparent',
                      color: item.active ? GREEN_DEEP : TXT_MUTED,
                    }}
                  >
                    <I className="h-4 w-4 shrink-0" />
                    {item.label}
                  </div>
                );
              })}
            </div>

            {/* Premium card */}
            <div className="px-2.5 py-3 mt-4 rounded-xl text-[11.5px]" style={{ backgroundColor: '#fff', border: `1px solid ${BORDER}` }}>
              <div className="font-bold mb-1 flex items-center gap-1" style={{ color: TXT }}>Passer à Premium <span style={{ color: GREEN_DEEP }}>✨</span></div>
              <div style={{ color: TXT_MUTED }}>Débloquez toutes les fonctionnalités avancées.</div>
            </div>

            {/* User */}
            <div className="mt-3 flex items-center gap-2.5 px-2 pt-3" style={{ borderTop: `1px solid ${BORDER}` }}>
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-[11px] font-bold" style={{ backgroundColor: GREEN_DEEP }}>AM</div>
              <div className="flex-1 min-w-0">
                <div className="text-[11.5px] font-bold truncate" style={{ color: TXT }}>Alex Martin</div>
                <div className="text-[10px] truncate" style={{ color: TXT_DIM }}>alex@martin.com</div>
              </div>
            </div>
          </div>

          {/* Main content */}
          <div className="flex-1 p-5 sm:p-6 min-w-0">
            <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
              <div>
                <div className="text-[18px] font-bold" style={{ color: TXT }}>Tableau de bord</div>
              </div>
              <div className="flex items-center gap-2.5">
                <span className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[12px]" style={{ color: TXT_MUTED, backgroundColor: '#fff', border: `1px solid ${BORDER}` }}>
                  📅 1 – 30 avril 2026
                </span>
                <button
                  className="inline-flex items-center gap-1.5 rounded-lg px-3.5 py-1.5 text-[12px] font-semibold text-white"
                  style={{ backgroundColor: GREEN }}
                >
                  + Nouvelle facture
                </button>
              </div>
            </div>

            {/* KPI row */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
              {[
                { label: "Chiffre d'affaires", value: '12 450,00 €', trend: '+24,5%', up: true },
                { label: 'Factures en attente', value: '8', sub: '2 850,00 €', neutral: true },
                { label: 'Factures payées', value: '23', trend: '+15,2%', up: true },
                { label: 'Dépenses', value: '4 230,00 €', trend: '−8,4%', up: false },
              ].map((kpi, i) => (
                <div key={i} className="rounded-xl p-3.5 bg-white" style={{ border: `1px solid ${BORDER}` }}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[10.5px] font-medium" style={{ color: TXT_MUTED }}>{kpi.label}</span>
                    {kpi.up !== undefined && (
                      kpi.up ? <TrendingUp className="h-3.5 w-3.5" style={{ color: GREEN_DEEP }} /> :
                      <TrendingDown className="h-3.5 w-3.5" style={{ color: '#DC2626' }} />
                    )}
                    {kpi.neutral && <Clock className="h-3.5 w-3.5" style={{ color: '#D97706' }} />}
                  </div>
                  <div className="text-[19px] font-bold tabular-nums" style={{ color: TXT }}>{kpi.value}</div>
                  {kpi.trend && (
                    <div className="text-[10.5px] font-semibold mt-0.5" style={{ color: kpi.up ? GREEN_DEEP : '#DC2626' }}>
                      {kpi.trend} ce mois
                    </div>
                  )}
                  {kpi.sub && <div className="text-[10.5px] mt-0.5" style={{ color: TXT_MUTED }}>{kpi.sub}</div>}
                </div>
              ))}
            </div>

            {/* Charts row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-5">
              {/* Evolution */}
              <div className="rounded-xl p-4" style={{ border: `1px solid ${BORDER}` }}>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[12.5px] font-semibold" style={{ color: TXT }}>Évolution du chiffre d'affaires</span>
                  <span className="text-[10px] rounded-md px-2 py-0.5" style={{ color: TXT_MUTED, backgroundColor: BG_SOFT, border: `1px solid ${BORDER}` }}>Ce mois</span>
                </div>
                <svg viewBox="0 0 360 80" className="w-full h-20">
                  <defs>
                    <linearGradient id="ev" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={GREEN} stopOpacity="0.3" />
                      <stop offset="100%" stopColor={GREEN} stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  <path d="M0 65 C 50 60, 80 55, 120 50 C 170 42, 210 38, 250 28 C 290 18, 320 15, 360 10 L 360 80 L 0 80 Z" fill="url(#ev)" />
                  <path d="M0 65 C 50 60, 80 55, 120 50 C 170 42, 210 38, 250 28 C 290 18, 320 15, 360 10" stroke={GREEN_DEEP} strokeWidth="2" fill="none" />
                </svg>
              </div>

              {/* Donut */}
              <div className="rounded-xl p-4" style={{ border: `1px solid ${BORDER}` }}>
                <div className="mb-3 text-[12.5px] font-semibold" style={{ color: TXT }}>Répartition par statut</div>
                <div className="flex items-center gap-5">
                  <svg viewBox="0 0 36 36" className="h-[90px] w-[90px] -rotate-90">
                    <circle cx="18" cy="18" r="15" fill="none" stroke="#F3F4F6" strokeWidth="5" />
                    <circle cx="18" cy="18" r="15" fill="none" stroke={GREEN} strokeWidth="5" strokeDasharray="68 100" strokeLinecap="round" />
                    <circle cx="18" cy="18" r="15" fill="none" stroke="#FBBF24" strokeWidth="5" strokeDasharray="19 100" strokeDashoffset="-68" strokeLinecap="round" />
                    <circle cx="18" cy="18" r="15" fill="none" stroke="#F87171" strokeWidth="5" strokeDasharray="13 100" strokeDashoffset="-87" strokeLinecap="round" />
                  </svg>
                  <div className="flex-1 space-y-1.5 text-[11.5px]">
                    {[
                      { label: 'Payées', value: '23 (68%)', color: GREEN },
                      { label: 'En attente', value: '6 (18%)', color: '#FBBF24' },
                      { label: 'En retard', value: '3 (14%)', color: '#F87171' },
                    ].map((s, i) => (
                      <div key={i} className="flex items-center justify-between">
                        <span className="inline-flex items-center gap-1.5" style={{ color: TXT_MUTED }}>
                          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} />
                          {s.label}
                        </span>
                        <span className="font-semibold" style={{ color: TXT }}>{s.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <div className="rounded-xl p-4" style={{ border: `1px solid ${BORDER}` }}>
                <div className="flex items-center justify-between mb-2.5">
                  <span className="text-[12.5px] font-semibold" style={{ color: TXT }}>Factures récentes</span>
                  <span className="text-[10px] font-semibold" style={{ color: GREEN_DEEP }}>Voir tout</span>
                </div>
                <div className="space-y-1.5 text-[11.5px]">
                  {[
                    { num: 'F-2026-042', client: 'Dupont SARL', amount: '2 450 €', status: 'Payée', color: GREEN },
                    { num: 'F-2026-041', client: 'Martin & Co', amount: '1 240 €', status: 'Envoyée', color: '#FBBF24' },
                    { num: 'F-2026-040', client: 'Bernard SAS', amount: '530 €', status: 'En retard', color: '#F87171' },
                  ].map((f, i) => (
                    <div key={i} className="flex items-center justify-between rounded-lg px-2.5 py-2" style={{ backgroundColor: BG_SOFT }}>
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText className="h-3.5 w-3.5 shrink-0" style={{ color: TXT_DIM }} />
                        <span className="font-semibold truncate" style={{ color: TXT }}>{f.num}</span>
                        <span className="truncate" style={{ color: TXT_DIM }}>{f.client}</span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="tabular-nums font-bold" style={{ color: TXT }}>{f.amount}</span>
                        <span className="px-1.5 py-0.5 rounded-md text-[9.5px] font-semibold" style={{ backgroundColor: `${f.color}22`, color: f.color }}>{f.status}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-xl p-4" style={{ border: `1px solid ${BORDER}` }}>
                <div className="flex items-center justify-between mb-2.5">
                  <span className="text-[12.5px] font-semibold" style={{ color: TXT }}>Top clients</span>
                  <span className="text-[10px] font-semibold" style={{ color: GREEN_DEEP }}>Voir tout</span>
                </div>
                <div className="space-y-2.5">
                  {[
                    { name: 'Dupont SARL', amount: '5 450 €', pct: 80 },
                    { name: 'Martin & Co', amount: '3 820 €', pct: 56 },
                    { name: 'Bernard SAS', amount: '2 650 €', pct: 38 },
                  ].map((c, i) => (
                    <div key={i}>
                      <div className="flex items-center justify-between mb-1 text-[11.5px]">
                        <span style={{ color: TXT }}>{c.name}</span>
                        <span className="tabular-nums font-bold" style={{ color: TXT }}>{c.amount}</span>
                      </div>
                      <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: BG_SOFT }}>
                        <div className="h-full rounded-full" style={{ width: `${c.pct}%`, background: `linear-gradient(90deg, ${GREEN_DEEP}, ${GREEN})` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </Container>
  </Section>
);

/* ━━━━━━━━━━━━━━━ ALTERNATING FEATURES ━━━━━━━━━━━━━━━ */
const Feature = ({ label, title, lead, bullets, mockup, testId, reverse = false, bg }) => (
  <Section testId={testId} bg={bg}>
    <Container>
      <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center ${reverse ? 'lg:[&>*:first-child]:order-2' : ''}`}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.7 }}
        >
          <Label>{label}</Label>
          <h3 className="mt-4 text-[32px] sm:text-[40px] lg:text-[46px] font-bold" style={{ color: TXT, letterSpacing: '-0.035em', lineHeight: 1.05 }}>
            {title}
          </h3>
          {lead && (
            <p className="mt-4 text-[16px]" style={{ color: TXT_MUTED, lineHeight: 1.6, maxWidth: '460px' }}>
              {lead}
            </p>
          )}
          <ul className="mt-6 space-y-3">
            {bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-3 text-[14.5px]" style={{ color: TXT_MUTED }}>
                <span className="mt-0.5 w-5 h-5 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: GREEN_SOFT }}>
                  <Check className="h-3 w-3" style={{ color: GREEN_DEEP, strokeWidth: 3 }} />
                </span>
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.7, delay: 0.1 }}
        >
          {mockup}
        </motion.div>
      </div>
    </Container>
  </Section>
);

/* ━━━━━━━━━━━━━━━ FEATURE MOCKUPS (light) ━━━━━━━━━━━━━━━ */
const cardLight = {
  backgroundColor: '#fff',
  border: `1px solid ${BORDER}`,
  boxShadow: '0 30px 70px -30px rgba(15,23,42,0.18)',
};

const FacturXMockup = () => (
  <div className="rounded-[24px] p-7" style={cardLight}>
    <div className="flex items-start justify-between mb-5">
      <div>
        <div className="text-[10.5px] font-bold uppercase tracking-[0.18em]" style={{ color: TXT_DIM }}>Facture</div>
        <div className="mt-1 text-[24px] font-bold" style={{ color: TXT, letterSpacing: '-0.02em' }}>F-2026-042</div>
        <div className="mt-1 text-[12.5px]" style={{ color: TXT_DIM }}>Émise le 12 janvier 2026</div>
      </div>
      <span className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider" style={{ backgroundColor: GREEN_SOFT, color: GREEN_DEEP }}>
        <Check className="h-3 w-3" /> Validée
      </span>
    </div>
    <div className="space-y-2">
      {[
        { label: 'Audit conformité PPF', price: '1 200,00' },
        { label: 'Mise en place Factur-X', price: '800,00' },
      ].map((line, i) => (
        <div key={i} className="flex items-center justify-between rounded-lg px-3.5 py-2.5" style={{ backgroundColor: BG_SOFT }}>
          <span className="text-[13px]" style={{ color: TXT_MUTED }}>{line.label}</span>
          <span className="text-[13px] font-semibold tabular-nums" style={{ color: TXT }}>{line.price} €</span>
        </div>
      ))}
    </div>
    <div className="mt-5 pt-5 grid grid-cols-3 gap-3" style={{ borderTop: `1px dashed ${BORDER}` }}>
      {[
        { label: 'Total HT', val: '2 000 €' },
        { label: 'TVA 20 %', val: '400 €' },
        { label: 'TTC', val: '2 400 €', accent: true },
      ].map((c, i) => (
        <div key={i}>
          <div className="text-[10px] font-bold uppercase tracking-wider" style={{ color: TXT_DIM }}>{c.label}</div>
          <div className="mt-1 text-[16px] font-bold" style={{ color: c.accent ? GREEN_DEEP : TXT }}>{c.val}</div>
        </div>
      ))}
    </div>
    <div className="mt-5 flex items-center justify-between text-[11.5px]" style={{ color: TXT_DIM }}>
      <span className="inline-flex items-center gap-1.5"><FileText className="h-3.5 w-3.5" /> PDF/A-3 + XML EN16931</span>
      <span style={{ color: GREEN_DEEP }}>Prêt pour le PPF</span>
    </div>
  </div>
);

const RelancesMockup = () => (
  <div className="rounded-[24px] p-5 space-y-2" style={cardLight}>
    {[
      { day: 'J+0', label: 'Facture envoyée', sub: 'Email envoyé · Lu', status: 'ok' },
      { day: 'J+7', label: 'Rappel courtois', sub: 'Email envoyé · Ouvert', status: 'ok' },
      { day: 'J+14', label: 'Relance ferme', sub: 'En cours · 2 jours', status: 'pending' },
      { day: 'J+30', label: 'Mise en demeure', sub: 'Programmée', status: 'queued' },
    ].map((r, i) => {
      const colors = {
        ok: { bg: GREEN_SOFT, icon: GREEN_DEEP, border: `${GREEN}55` },
        pending: { bg: '#FEF3C7', icon: '#D97706', border: '#FBBF2466' },
        queued: { bg: BG_SOFT, icon: TXT_DIM, border: BORDER },
      }[r.status];
      return (
        <div key={i} className="flex items-center gap-3.5 rounded-xl px-4 py-3" style={{ backgroundColor: colors.bg, border: `1px solid ${colors.border}` }}>
          <div className="w-9 h-9 rounded-lg bg-white flex items-center justify-center shrink-0">
            {r.status === 'ok' ? <Check className="h-4 w-4" style={{ color: colors.icon, strokeWidth: 3 }} /> : r.status === 'pending' ? <Clock className="h-4 w-4" style={{ color: colors.icon }} /> : <Bell className="h-4 w-4" style={{ color: colors.icon }} />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[13.5px] font-semibold" style={{ color: TXT }}>{r.label}</div>
            <div className="text-[11.5px] mt-0.5" style={{ color: TXT_MUTED }}>{r.sub}</div>
          </div>
          <span className="text-[10.5px] font-bold uppercase tracking-wider bg-white px-2 py-1 rounded-full" style={{ color: TXT_MUTED }}>{r.day}</span>
        </div>
      );
    })}
  </div>
);

const UrssafMockup = () => (
  <div className="rounded-[24px] p-7" style={cardLight}>
    <div className="flex items-center justify-between mb-5">
      <div>
        <div className="text-[10.5px] font-bold uppercase tracking-[0.18em]" style={{ color: TXT_DIM }}>Trimestre T1 2026</div>
        <div className="mt-1 text-[19px] font-bold" style={{ color: TXT, letterSpacing: '-0.02em' }}>Cotisations estimées</div>
      </div>
      <Calculator className="h-5 w-5" style={{ color: GREEN_DEEP }} />
    </div>
    <div>
      <div className="flex items-center justify-between mb-2 text-[12px]">
        <span style={{ color: TXT_MUTED }}>Chiffre d'affaires</span>
        <span className="font-semibold tabular-nums" style={{ color: TXT }}>12 480 €</span>
      </div>
      <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: BG_SOFT }}>
        <div className="h-full rounded-full" style={{ width: '78%', background: `linear-gradient(90deg, ${GREEN_DEEP}, ${GREEN})` }} />
      </div>
    </div>
    <div className="mt-5 grid grid-cols-2 gap-3">
      <div className="rounded-xl p-4" style={{ backgroundColor: GREEN_SOFT }}>
        <div className="text-[10px] font-bold uppercase tracking-wider" style={{ color: GREEN_DEEP }}>Cotisations</div>
        <div className="mt-1.5 text-[22px] font-bold tabular-nums" style={{ color: TXT }}>2 745 €</div>
        <div className="mt-0.5 text-[11px]" style={{ color: TXT_MUTED }}>22 % du CA</div>
      </div>
      <div className="rounded-xl p-4" style={{ backgroundColor: '#FEF3C7' }}>
        <div className="text-[10px] font-bold uppercase tracking-wider" style={{ color: '#92400E' }}>Net pour vous</div>
        <div className="mt-1.5 text-[22px] font-bold tabular-nums" style={{ color: TXT }}>9 735 €</div>
        <div className="mt-0.5 text-[11px]" style={{ color: TXT_MUTED }}>78 % conservé</div>
      </div>
    </div>
    <div className="mt-5 flex items-center gap-2.5 rounded-xl px-3.5 py-3" style={{ backgroundColor: BG_SOFT, border: `1px solid ${BORDER}` }}>
      <Clock className="h-4 w-4" style={{ color: '#D97706' }} />
      <span className="text-[12.5px]" style={{ color: TXT_MUTED }}>Échéance · 30 avril · Rappel programmé</span>
    </div>
  </div>
);

const BankMockup = () => (
  <div className="rounded-[24px] p-6" style={cardLight}>
    <div className="flex items-center justify-between mb-4">
      <div className="text-[15px] font-bold" style={{ color: TXT, letterSpacing: '-0.02em' }}>Rapprochement bancaire</div>
      <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-wider" style={{ backgroundColor: GREEN_SOFT, color: GREEN_DEEP }}>
        <Sparkles className="h-3 w-3" /> IA active
      </span>
    </div>
    <div className="space-y-2">
      {[
        { client: 'Acme SARL', amount: '2 400,00', match: true },
        { client: 'Studio Lumière', amount: '1 240,00', match: true },
        { client: 'Atelier Nord', amount: '530,00', match: true },
        { client: 'Versement non identifié', amount: '320,00', match: false },
      ].map((r, i) => (
        <div key={i} className="flex items-center gap-3 rounded-lg px-3 py-2.5" style={{ backgroundColor: BG_SOFT, border: `1px solid ${BORDER}` }}>
          <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: r.match ? GREEN_SOFT : '#FEF3C7' }}>
            {r.match ? <Check className="h-4 w-4" style={{ color: GREEN_DEEP, strokeWidth: 3 }} /> : <Clock className="h-4 w-4" style={{ color: '#D97706' }} />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[12.5px] font-semibold truncate" style={{ color: TXT }}>{r.client}</div>
            <div className="text-[11px]" style={{ color: TXT_DIM }}>{r.match ? 'Rapprochée automatiquement' : 'À vérifier'}</div>
          </div>
          <span className="text-[12.5px] font-semibold tabular-nums" style={{ color: r.match ? TXT : '#D97706' }}>+{r.amount} €</span>
        </div>
      ))}
    </div>
    <div className="mt-4 flex items-center justify-between text-[11.5px]" style={{ color: TXT_MUTED }}>
      <span>Matching automatique en cours</span>
      <span style={{ color: GREEN_DEEP, fontWeight: 600 }}>Voir tout →</span>
    </div>
  </div>
);

/* ━━━━━━━━━━━━━━━ INDUSTRIES ━━━━━━━━━━━━━━━ */
const INDUSTRIES = [
  { name: 'Freelance', icon: Laptop },
  { name: 'Artisan BTP', icon: HardHat },
  { name: 'Consultant', icon: Briefcase },
  { name: 'E-commerce', icon: ShoppingBag },
  { name: 'Profession libérale', icon: Stethoscope },
  { name: 'Auto-entrepreneur', icon: Palette },
];

const Industries = () => (
  <Section id="metiers" bg={BG_SOFT} testId="industries-section">
    <Container>
      <div className="text-center mx-auto" style={{ maxWidth: '680px' }}>
        <Label>Pour votre métier</Label>
        <h2 className="mt-4 text-[36px] sm:text-[48px] lg:text-[54px] font-bold" style={{ color: TXT, letterSpacing: '-0.035em', lineHeight: 1.05 }}>
          Pensé pour vous.
        </h2>
        <p className="mt-5 text-[16px]" style={{ color: TXT_MUTED, lineHeight: 1.6 }}>
          Quelle que soit votre activité, SmartFacture s'adapte à votre régime.
        </p>
      </div>

      <div className="mt-12 grid grid-cols-2 lg:grid-cols-3 gap-4">
        {INDUSTRIES.map((ind, i) => {
          const I = ind.icon;
          const slug = ind.name.toLowerCase().replace(/\s+/g, '-');
          return (
            <motion.div
              key={ind.name}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05, duration: 0.5 }}
              data-testid={`industry-${slug}`}
              className="group flex items-center justify-between rounded-2xl px-5 py-5 sm:px-6 sm:py-6 cursor-pointer transition-all duration-300 hover:-translate-y-0.5"
              style={{ backgroundColor: '#fff', border: `1px solid ${BORDER}` }}
            >
              <div className="flex items-center gap-3.5">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: GREEN_SOFT }}>
                  <I className="h-5 w-5" style={{ color: GREEN_DEEP }} />
                </div>
                <span className="text-[15px] font-semibold" style={{ color: TXT }}>{ind.name}</span>
              </div>
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" style={{ color: TXT_DIM }} />
            </motion.div>
          );
        })}
      </div>
    </Container>
  </Section>
);

/* ━━━━━━━━━━━━━━━ TRUST — REAL ONLY (no fake ratings) ━━━━━━━━━━━━━━━ */
const Trust = () => (
  <Section testId="trust-section">
    <Container narrow className="text-center">
      <Label>Sécurité &amp; conformité</Label>
      <h2 className="mt-4 text-[34px] sm:text-[44px] font-bold" style={{ color: TXT, letterSpacing: '-0.035em', lineHeight: 1.1 }}>
        Vos données.<br />Sous bonne garde.
      </h2>
      <p className="mt-5 text-[16px]" style={{ color: TXT_MUTED, lineHeight: 1.6 }}>
        Standards bancaires appliqués à votre comptabilité.
      </p>

      <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { title: 'Hébergement France', desc: 'OVHcloud · Roubaix', icon: Server },
          { title: 'Chiffrement AES-256', desc: 'Au repos &amp; en transit', icon: Lock },
          { title: 'Conformité RGPD', desc: 'Données sous votre contrôle', icon: ShieldCheck },
          { title: 'Factur-X EN16931', desc: 'Format légal 2026', icon: Award },
        ].map((r, i) => {
          const I = r.icon;
          return (
            <div
              key={i}
              data-testid={`trust-card-${i}`}
              className="rounded-2xl p-6 text-left"
              style={{ backgroundColor: '#fff', border: `1px solid ${BORDER}`, boxShadow: '0 10px 30px -15px rgba(15,23,42,0.06)' }}
            >
              <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: GREEN_SOFT }}>
                <I className="h-5 w-5" style={{ color: GREEN_DEEP }} />
              </div>
              <div className="text-[15px] font-bold" style={{ color: TXT }}>{r.title}</div>
              <div className="text-[12.5px] mt-1" style={{ color: TXT_MUTED }} dangerouslySetInnerHTML={{ __html: r.desc }} />
            </div>
          );
        })}
      </div>
    </Container>
  </Section>
);

/* ━━━━━━━━━━━━━━━ PRICING ━━━━━━━━━━━━━━━ */
const PRICING = [
  {
    name: 'Découverte', price: '0', tagline: 'Sans CB · pour démarrer',
    features: ['5 factures / mois', 'Factur-X automatique', 'Export PDF & CSV'],
    testId: 'plan-free',
    free: true,
  },
  {
    name: 'Starter', price: '14', tagline: 'Auto-entrepreneurs',
    features: ['20 factures / mois', 'Tout Découverte inclus', 'Simulateur URSSAF', 'Support email'],
    testId: 'plan-starter',
  },
  {
    name: 'Pro', price: '29', tagline: 'Le plus choisi',
    features: ['Factures illimitées', 'Relances automatiques', 'Module URSSAF complet', 'Scanner IA OCR', 'Support prioritaire'],
    popular: true, testId: 'plan-pro',
  },
  {
    name: 'Expert', price: '49', tagline: 'Multi-utilisateurs',
    features: ['Tout Pro inclus', '5 utilisateurs', 'Accès comptable', 'API & webhooks', 'Support dédié'],
    testId: 'plan-expert',
  },
];

const Pricing = () => (
  <Section id="tarifs" bg={BG_SOFT} testId="pricing-section">
    <Container>
      <div className="text-center mx-auto" style={{ maxWidth: '680px' }}>
        <Label>Tarifs</Label>
        <h2 className="mt-4 text-[40px] sm:text-[52px] font-bold" style={{ color: TXT, letterSpacing: '-0.04em', lineHeight: 1.05 }}>
          Simple. Transparent.
        </h2>
        <p className="mt-5 text-[16px]" style={{ color: TXT_MUTED, lineHeight: 1.6 }}>
          Commencez gratuitement. Évoluez quand vous voulez. Sans engagement.
        </p>
      </div>

      <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
        {PRICING.map((p) => (
          <div
            key={p.name}
            data-testid={p.testId}
            className="relative rounded-2xl p-6 transition-transform duration-300 hover:-translate-y-1 flex flex-col"
            style={{
              backgroundColor: '#fff',
              border: p.popular ? `2px solid ${GREEN}` : `1px solid ${BORDER}`,
              boxShadow: p.popular ? `0 30px 60px -30px ${GREEN_GLOW}` : '0 15px 40px -30px rgba(15,23,42,0.1)',
            }}
          >
            {p.popular && (
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full px-3 py-1 text-[10.5px] font-bold uppercase tracking-wider text-white" style={{ backgroundColor: GREEN, letterSpacing: '0.15em' }}>
                Le plus choisi
              </span>
            )}
            {p.free && (
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full px-3 py-1 text-[10.5px] font-bold uppercase tracking-wider" style={{ backgroundColor: GREEN_SOFT, color: GREEN_DEEP, letterSpacing: '0.15em' }}>
                Gratuit
              </span>
            )}
            <div className="text-[12px] font-bold uppercase tracking-[0.18em]" style={{ color: p.popular ? GREEN_DEEP : TXT_DIM }}>{p.name}</div>
            <div className="mt-1 text-[12.5px]" style={{ color: TXT_MUTED }}>{p.tagline}</div>
            <div className="mt-5 flex items-baseline gap-1">
              <span className="text-[44px] leading-none font-bold" style={{ color: TXT, letterSpacing: '-0.04em' }}>{p.price}€</span>
              <span className="text-[13px]" style={{ color: TXT_DIM }}>{p.free ? '' : '/mois'}</span>
            </div>
            <ul className="mt-5 space-y-2 flex-1">
              {p.features.map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-[13.5px]" style={{ color: TXT_MUTED }}>
                  <Check className="h-4 w-4 mt-0.5 shrink-0" style={{ color: GREEN_DEEP, strokeWidth: 3 }} />
                  {f}
                </li>
              ))}
            </ul>
            <Link
              to="/register"
              data-testid={`${p.testId}-cta`}
              className="mt-6 inline-flex items-center justify-center w-full h-11 rounded-full font-semibold text-[14px] transition-colors"
              style={{
                backgroundColor: p.popular ? GREEN : '#fff',
                color: p.popular ? '#fff' : TXT,
                border: p.popular ? 'none' : `1px solid ${BORDER}`,
              }}
              onMouseOver={(e) => {
                if (p.popular) e.currentTarget.style.backgroundColor = GREEN_DEEP;
                else e.currentTarget.style.backgroundColor = '#F9FAFB';
              }}
              onMouseOut={(e) => {
                if (p.popular) e.currentTarget.style.backgroundColor = GREEN;
                else e.currentTarget.style.backgroundColor = '#fff';
              }}
            >
              {p.free ? 'Commencer gratuitement' : 'Démarrer'}
            </Link>
          </div>
        ))}
      </div>

      <p className="mt-10 text-center text-[13px]" style={{ color: TXT_DIM }}>
        Garantie 30 jours satisfait ou remboursé · Annulation en 1 clic
      </p>
    </Container>
  </Section>
);

/* ━━━━━━━━━━━━━━━ FAQ ━━━━━━━━━━━━━━━ */
const FAQ_ITEMS = [
  { q: 'Compatible micro-entrepreneur ?', a: 'Oui, totalement. SmartFacture est pensé pour les régimes BNC, micro-entreprise, EI, SAS, SASU, SARL et SCI. La TVA est gérée automatiquement selon votre régime (franchise en base incluse).' },
  { q: 'Mon comptable peut-il accéder ?', a: 'Oui, dès le plan Pro. Votre expert-comptable accède en lecture seule à vos factures, exports CSV/Excel et journaux comptables.' },
  { q: 'Après les 14 jours d\'essai, que se passe-t-il ?', a: 'Si vous n\'avez pas saisi de carte bancaire, votre compte bascule en lecture seule (vous gardez l\'accès complet à vos données). Si vous avez activé un plan, le prélèvement démarre à la fin de l\'essai. Annulation toujours possible en 1 clic.' },
  { q: 'Vraiment conforme PPF / Factur-X ?', a: 'Oui. Toutes les factures sont au format Factur-X (PDF/A-3 + XML EN16931), mentions obligatoires CGI incluses, compatibles PPF et toute PDP agréée. Vous êtes prêt pour le 1ᵉʳ septembre 2026.' },
  { q: 'Import depuis mon ancienne solution ?', a: 'Oui. Import CSV/Excel de vos clients et factures existantes en quelques clics. Notre équipe vous accompagne gratuitement sur les imports complexes via le chat support.' },
  { q: 'Données hébergées où ?', a: 'Données stockées chez OVHcloud à Roubaix, France. Chiffrement AES-256 au repos et en transit. Conformité RGPD totale.' },
];

const FAQ = () => (
  <Section id="ressources" testId="faq-section">
    <Container narrow>
      <div className="text-center">
        <Label>FAQ</Label>
        <h2 className="mt-4 text-[36px] sm:text-[48px] font-bold" style={{ color: TXT, letterSpacing: '-0.04em', lineHeight: 1.05 }}>
          Vos questions,<br /><span style={{ color: TXT_DIM }}>nos réponses.</span>
        </h2>
      </div>

      <Accordion type="single" collapsible className="mt-12 space-y-2.5">
        {FAQ_ITEMS.map((item, i) => (
          <AccordionItem
            key={i}
            value={`item-${i}`}
            data-testid={`faq-item-${i}`}
            className="rounded-2xl px-6 transition-colors"
            style={{ backgroundColor: '#fff', border: `1px solid ${BORDER}` }}
          >
            <AccordionTrigger className="text-left text-[15.5px] font-semibold hover:no-underline py-5" style={{ color: TXT }}>
              {item.q}
            </AccordionTrigger>
            <AccordionContent className="text-[14.5px] pb-5" style={{ color: TXT_MUTED, lineHeight: 1.65 }}>
              {item.a}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </Container>
  </Section>
);

/* ━━━━━━━━━━━━━━━ FINAL CTA ━━━━━━━━━━━━━━━ */
const FinalCTA = () => (
  <Section testId="final-cta" id="apropos">
    <Container>
      <div
        className="relative overflow-hidden rounded-[28px] px-8 py-16 sm:px-16 sm:py-20 text-center"
        style={{
          background: `linear-gradient(135deg, ${GREEN_SOFT} 0%, #FFFFFF 60%, ${GREEN_SOFT} 100%)`,
          border: `1px solid ${BORDER}`,
        }}
      >
        <div className="absolute inset-0 -z-0 pointer-events-none">
          <div className="absolute top-[-50%] left-1/2 -translate-x-1/2 w-[600px] h-[400px] rounded-full opacity-40" style={{ background: `radial-gradient(closest-side, ${GREEN_GLOW} 0%, transparent 70%)` }} />
        </div>
        <div className="relative">
          <h2 className="text-[36px] sm:text-[52px] lg:text-[60px] font-bold mx-auto" style={{ color: TXT, letterSpacing: '-0.04em', lineHeight: 1.05, maxWidth: '680px' }}>
            Prêt en <span style={{ color: GREEN_DEEP }}>90 secondes.</span>
          </h2>
          <p className="mt-5 text-[16px] mx-auto" style={{ color: TXT_MUTED, maxWidth: '520px', lineHeight: 1.6 }}>
            Créez votre compte, ajoutez votre premier client, émettez votre première facture conforme.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row justify-center gap-3">
            <PrimaryBtn to="/register" testId="final-cta-register" large>
              Essayer gratuitement
            </PrimaryBtn>
            <GhostBtn to="/simulateur" testId="final-cta-simulator" large>
              Tester ma conformité
            </GhostBtn>
          </div>
          <p className="mt-6 text-[13px]" style={{ color: TXT_DIM }}>
            14 jours gratuits · Sans CB · Annulation en 1 clic
          </p>
        </div>
      </div>
    </Container>
  </Section>
);

/* ━━━━━━━━━━━━━━━ FOOTER ━━━━━━━━━━━━━━━ */
const Footer = () => (
  <footer
    data-testid="landing-footer"
    style={{ backgroundColor: BG_SOFT, paddingTop: '70px', paddingBottom: '40px', borderTop: `1px solid ${BORDER}` }}
    className="px-5 sm:px-6"
  >
    <Container>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-10">
        <div className="col-span-2 md:col-span-1">
          <Link to="/" className="inline-block mb-4">
            <img src="/smartfacture-logo.png" alt="SmartFacture AI" className="h-10 w-auto" />
          </Link>
          <p className="text-[13.5px]" style={{ color: TXT_MUTED, lineHeight: 1.6 }}>
            Le copilote facturation des indépendants français.
          </p>
        </div>
        <div>
          <h4 className="text-[12px] font-bold uppercase tracking-[0.18em] mb-4" style={{ color: TXT_DIM }}>Produit</h4>
          <ul className="space-y-2.5 text-[13.5px]" style={{ color: TXT_MUTED }}>
            <li><Link to="/pricing" className="hover:text-slate-900 transition-colors">Tarifs</Link></li>
            <li><a href="#fonctionnalites" className="hover:text-slate-900 transition-colors">Fonctionnalités</a></li>
            <li><Link to="/simulateur" className="hover:text-slate-900 transition-colors">Test conformité</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="text-[12px] font-bold uppercase tracking-[0.18em] mb-4" style={{ color: TXT_DIM }}>Ressources</h4>
          <ul className="space-y-2.5 text-[13.5px]" style={{ color: TXT_MUTED }}>
            <li><Link to="/blog" className="hover:text-slate-900 transition-colors">Blog</Link></li>
            <li><Link to="/guide-efacturation" className="hover:text-slate-900 transition-colors">Guide e-facturation</Link></li>
            <li><Link to="/qui-sommes-nous" className="hover:text-slate-900 transition-colors">À propos</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="text-[12px] font-bold uppercase tracking-[0.18em] mb-4" style={{ color: TXT_DIM }}>Légal</h4>
          <ul className="space-y-2.5 text-[13.5px]" style={{ color: TXT_MUTED }}>
            <li><Link to="/mentions-legales" className="hover:text-slate-900 transition-colors">Mentions légales</Link></li>
            <li><Link to="/cgu" className="hover:text-slate-900 transition-colors">CGU</Link></li>
            <li><Link to="/confidentialite" className="hover:text-slate-900 transition-colors">Confidentialité</Link></li>
          </ul>
        </div>
      </div>

      <div className="mt-14 pt-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-[12.5px]" style={{ borderTop: `1px solid ${BORDER}`, color: TXT_DIM }}>
        <p>© 2026 SmartFacture AI · Tous droits réservés.</p>
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: GREEN }} />
            Conforme e-facturation 2026
          </span>
        </div>
      </div>
    </Container>
  </footer>
);

/* ━━━━━━━━━━━━━━━ MAIN ━━━━━━━━━━━━━━━ */
const LandingPage = () => {
  useEffect(() => {
    const prev = document.body.style.backgroundColor;
    document.body.style.backgroundColor = BG;
    return () => { document.body.style.backgroundColor = prev; };
  }, []);

  return (
    <div className="landing-v2 min-h-screen" style={{ backgroundColor: BG, color: TXT }}>
      <Navbar />
      <Hero />
      <TrustStrip />
      <DashboardPreview />

      <Feature
        testId="feature-facturx"
        label="Conforme 2026"
        title="Vos factures, conformes. Automatiquement."
        lead="Le format Factur-X est généré à chaque émission. Vous, vous facturez. Le reste est invisible."
        bullets={[
          'PDF/A-3 + XML EN16931 embarqué',
          'Numérotation séquentielle automatique',
          'Mentions légales CGI pré-remplies',
          'Compatible PPF & toute PDP agréée',
        ]}
        mockup={<FacturXMockup />}
      />

      <Feature
        testId="feature-relances" reverse bg={BG_SOFT}
        label="Trésorerie"
        title="Vos clients paient. Sans que vous demandiez."
        lead="Relances automatiques J+7, J+14, J+30. Tonalité ajustée. Vos retards se résorbent tout seuls."
        bullets={[
          'Séquences personnalisables par client',
          'Détection automatique des paiements reçus',
          'Mise en demeure prête à signer',
          'Statistiques d\'efficacité par séquence',
        ]}
        mockup={<RelancesMockup />}
      />

      <Feature
        testId="feature-urssaf"
        label="Cotisations"
        title="Plus jamais de surprise URSSAF."
        lead="Le simulateur calcule vos cotisations en temps réel selon votre statut. Vous voyez votre net avant l'échéance."
        bullets={[
          'Calcul automatique selon votre régime',
          'Rappels d\'échéance en amont',
          'Estimation du net après cotisations',
          'Historique exportable pour comptable',
        ]}
        mockup={<UrssafMockup />}
      />

      <Feature
        testId="feature-banque" reverse bg={BG_SOFT}
        label="Pré-comptabilité"
        title="L'IA rapproche. Vous validez."
        lead="Importez votre relevé. L'IA matche vos factures et vos paiements. Le rapprochement n'est plus votre problème."
        bullets={[
          'Import CSV BNP, Crédit Agricole, Société Générale, Qonto…',
          'Matching automatique factures ↔ paiements',
          'Identification des paiements partiels',
          'Export comptable CSV / Excel en 1 clic',
        ]}
        mockup={<BankMockup />}
      />

      <Industries />
      <Trust />
      <Pricing />
      <FAQ />
      <FinalCTA />
      <Footer />

      <SupportAssistant />
    </div>
  );
};

export default LandingPage;
